import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { Palette, X, Plus } from "@phosphor-icons/react";

/**
 * Brand Profile editor. Lives inside the Settings tab so operators can
 * tune the voice + banned words + colors without touching backend code.
 * Updates are PATCHed against /api/content-studio/brand-profile.
 */
export default function BrandProfileCard() {
    const [profile, setProfile] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let cancelled = false;
        api.get("/content-studio/brand-profile")
            .then(r => { if (!cancelled) setProfile(r.data); })
            .catch(() => toast.error("Could not load brand profile"));
        return () => { cancelled = true; };
    }, []);

    const patch = async (changes) => {
        setBusy(true);
        try {
            const r = await api.patch("/content-studio/brand-profile", changes);
            setProfile(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setBusy(false);
        }
    };

    const addToList = (key, value) => {
        const v = value.trim();
        if (!v) return;
        const list = profile[key] || [];
        if (list.includes(v)) return;
        patch({ [key]: [...list, v] });
    };
    const removeFromList = (key, idx) => {
        const list = [...(profile[key] || [])];
        list.splice(idx, 1);
        patch({ [key]: list });
    };

    if (!profile) return <p className="text-xs text-muted-foreground">Loading brand profile…</p>;

    return (
        <section className="border border-border bg-card rounded-sm p-6 mb-6" data-testid="brand-profile-card">
            <header className="mb-4 pb-3 border-b border-border flex items-center gap-2">
                <Palette size={18} weight="duotone" className="text-primary" />
                <div>
                    <h3 className="font-display text-lg font-bold leading-tight">Brand Profile</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                        Voice rules + banned/preferred words are injected into every LLM call. Colors + fonts drive the template renderer.
                    </p>
                </div>
            </header>

            {/* Voice block */}
            <Field label="Brand voice (paragraph injected into every prompt)">
                <Textarea
                    data-testid="brand-voice"
                    rows={4}
                    defaultValue={profile.voice_block}
                    onBlur={(e) => e.target.value !== profile.voice_block && patch({ voice_block: e.target.value })}
                    className="rounded-sm"
                />
            </Field>

            {/* Colors */}
            <div className="grid grid-cols-5 gap-3 mt-4">
                {["color_paper","color_amber","color_teal","color_navy","color_ink"].map(k => (
                    <div key={k}>
                        <label className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground block mb-1.5">{k.replace("color_","")}</label>
                        <div className="flex items-center gap-2">
                            <span className="w-7 h-7 rounded-sm border border-border" style={{ background: `#${profile[k]}` }} />
                            <Input
                                data-testid={`brand-${k}`}
                                defaultValue={profile[k]}
                                onBlur={(e) => e.target.value !== profile[k] && patch({ [k]: e.target.value.replace(/^#/, "") })}
                                className="rounded-sm h-8 text-xs font-mono"
                            />
                        </div>
                    </div>
                ))}
            </div>

            {/* Banned / preferred words */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <WordList
                    label="Banned words"
                    description="Hard-banned in every generation. Includes variants."
                    testid="banned-words"
                    values={profile.banned_words || []}
                    onAdd={(v) => addToList("banned_words", v)}
                    onRemove={(i) => removeFromList("banned_words", i)}
                />
                <WordList
                    label="Preferred HAEVN terminology"
                    description="The LLM is told to use these when relevant."
                    testid="preferred-words"
                    values={profile.preferred_words || []}
                    onAdd={(v) => addToList("preferred_words", v)}
                    onRemove={(i) => removeFromList("preferred_words", i)}
                />
            </div>

            {/* Logos */}
            <div className="mt-6 pt-6 border-t border-border">
                <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1">Brand logos</p>
                <p className="text-[11px] text-muted-foreground mb-3">PNG / JPG / SVG, max 10MB. The template renderer overlays the master logo on visual assets when one is set.</p>
                <div className="grid grid-cols-3 gap-3" data-testid="brand-logos">
                    {[
                        { kind: "logo_master",    label: "Master" },
                        { kind: "logo_secondary", label: "Secondary" },
                        { kind: "logo_icon",      label: "Icon / mark" },
                    ].map(({ kind, label }) => (
                        <LogoSlot
                            key={kind}
                            kind={kind}
                            label={label}
                            currentPath={profile[`${kind}_path`]}
                            onChange={(path) => { setProfile({ ...profile, [`${kind}_path`]: path }); }}
                        />
                    ))}
                </div>
            </div>

            {busy && <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mt-3">Saving…</p>}
        </section>
    );
}

function LogoSlot({ kind, label, currentPath, onChange }) {
    const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
    const [uploading, setUploading] = useState(false);

    const onPick = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) { toast.error("File too large (max 10MB)"); return; }
        setUploading(true);
        try {
            const form = new FormData();
            form.append("kind", kind);
            form.append("file", file);
            const token = localStorage.getItem("haevn:auth_token");
            const resp = await fetch(`${apiBase}/content-studio/brand-assets/upload`, {
                method: "POST",
                body: form,
                headers: { "Authorization": `Bearer ${token}` },
            });
            if (!resp.ok) {
                const err = await resp.json().catch(() => ({}));
                throw new Error(err.detail || `Upload failed (${resp.status})`);
            }
            const data = await resp.json();
            onChange(data.file_name);
            toast.success(`${label} logo uploaded`);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    const remove = async () => {
        if (!window.confirm(`Remove the ${label} logo?`)) return;
        try {
            await api.patch("/content-studio/brand-profile", { [`${kind}_path`]: "" });
            onChange("");
            toast.success(`${label} logo cleared`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not clear logo");
        }
    };

    return (
        <div className="border border-border rounded-sm bg-background p-3" data-testid={`logo-slot-${kind}`}>
            <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-2">{label}</p>
            {currentPath ? (
                <div className="aspect-square w-full bg-foreground/90 rounded-sm overflow-hidden mb-2 flex items-center justify-center">
                    <img
                        data-testid={`logo-preview-${kind}`}
                        src={`${apiBase}/content-studio/brand-assets/file/${currentPath}`}
                        alt={label}
                        className="max-w-full max-h-full object-contain p-2"
                    />
                </div>
            ) : (
                <div className="aspect-square w-full border border-dashed border-border rounded-sm mb-2 flex items-center justify-center">
                    <span className="text-[10px] text-muted-foreground">No logo</span>
                </div>
            )}
            <div className="flex items-center gap-1">
                <label className="flex-1">
                    <input type="file" accept="image/*" onChange={onPick} className="hidden" data-testid={`logo-input-${kind}`} />
                    <span className="text-[10px] font-mono uppercase tracking-wider text-primary hover:underline cursor-pointer block text-center py-1 border border-primary/40 rounded-sm">
                        {uploading ? "Uploading…" : (currentPath ? "Replace" : "Upload")}
                    </span>
                </label>
                {currentPath && (
                    <button
                        type="button"
                        data-testid={`logo-remove-${kind}`}
                        onClick={remove}
                        className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground hover:text-accent px-2 py-1"
                    >
                        clear
                    </button>
                )}
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground block mb-1.5">{label}</label>
            {children}
        </div>
    );
}

function WordList({ label, description, testid, values, onAdd, onRemove }) {
    const [input, setInput] = useState("");
    const submit = () => { onAdd(input); setInput(""); };
    return (
        <div>
            <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{label}</p>
            <p className="text-[11px] text-muted-foreground mt-0.5 mb-2">{description}</p>
            <div className="flex flex-wrap gap-1.5" data-testid={testid}>
                {values.map((v, i) => (
                    <span key={i} className="inline-flex items-center gap-1 text-[11px] font-mono bg-muted px-2 py-0.5 rounded-sm">
                        {v}
                        <button onClick={() => onRemove(i)} className="text-muted-foreground hover:text-accent">
                            <X size={10} />
                        </button>
                    </span>
                ))}
            </div>
            <div className="flex items-center gap-1.5 mt-2">
                <Input
                    data-testid={`${testid}-input`}
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
                    placeholder="Add word and press Enter"
                    className="rounded-sm h-8 text-xs flex-1"
                />
                <Button variant="outline" size="sm" className="rounded-sm h-8 px-2" onClick={submit}>
                    <Plus size={12} />
                </Button>
            </div>
        </div>
    );
}
